import { addWeeks, subDays } from 'date-fns'
import isEqual from 'lodash/isEqual'
import { action, computed, extendObservable, makeObservable, observable } from 'mobx'
import { computedRouteParam } from '@stores/computedRouteParam'

import {
  BookingIssue,
  PetPolicyFieldKind,
  RevenueModel,
  RiskLevel,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatDate } from '@avantstay/backoffice-core'

import { TapeChartSidebarFilters } from '@new/domains/bookingsTapeChart/BookingsTapeChart.types'

import { RoutingStore } from '@stores/RoutingStore'

const today = new Date()
export const DEFAULT_DATE_RANGE = `${formatDate(subDays(today, 1))}_${formatDate(
  addWeeks(today, 4),
)}`

export interface TapeChartFiltersStoreBase extends OtherTapeChartFiltersStore {
  sidebarFiltersFromUrl: Promise<TapeChartSidebarFilters>
  applyCachedFilters: () => void
  clearOtherFilters: () => void
}

export abstract class OtherTapeChartFiltersStore {
  filtersCache: TapeChartSidebarFilters = {}

  activeFiltersLength = 0

  amenityIdsFilterParam

  bookingIssuesFilterParam

  dateRangeFilterParam

  depositsOnHoldParam

  exactBedroomsFilterParam

  highlightIdsFilterParam

  maxPriceFilterParam

  minBathroomsFilterParam

  minBedroomsFilterParam

  minPriceFilterParam

  petsFilterParam

  propertyIdParam

  revenueModelFilterParam

  riskLevelFilterParam

  seasonGroupsFilterParam

  showNightlyRateFilterParam

  minOccupancyParam

  maxOccupancyParam

  protected constructor(routePaths: string[]) {
    makeObservable(this, {
      filtersCache: observable,
      activeFiltersLength: observable,
      otherTapeChartFiltersFromUrl: computed,
      setActiveFiltersLength: action.bound,
      setFiltersCache: action.bound,
      setAmenityIdsFilter: action.bound,
      setBookingIssuesFilter: action.bound,
      setDateRangeFilter: action.bound,
      setExactBedroomsFilter: action.bound,
      setSeasonGroupIdsFilter: action.bound,
      setMinBathroomsFilter: action.bound,
      setMinBedroomsFilter: action.bound,
      setPetsFilter: action.bound,
      setDepositsOnHoldFilter: action.bound,
      setMinPriceFilter: action.bound,
      setMaxPriceFilter: action.bound,
      setShowNightlyRateFilter: action.bound,
      setHighlightIdsFilter: action.bound,
      setRevenueModelFilter: action.bound,
      setRiskLevelFilter: action.bound,
      setMinOccupancy: action.bound,
      setMaxOccupancy: action.bound,
      superApplyCachedFilters: action.bound,
      superClearOtherFilters: action.bound,
      clearFilterByKey: action.bound,
    })

    this.amenityIdsFilterParam = computedRouteParam<string[] | undefined>('amenities', {
      patterns: routePaths,
      format: (values?: string[] | null) => (values ? values.join(',') : ''),
      parse: valueString => (valueString ? valueString.split(',') : undefined),
    })

    this.bookingIssuesFilterParam = computedRouteParam<BookingIssue[] | undefined>('issues', {
      patterns: routePaths,
      format: (values?: string[] | null) => (values ? values.join(',') : ''),
      parse: valueString => (valueString ? (valueString.split(',') as BookingIssue[]) : undefined),
    })

    this.dateRangeFilterParam = computedRouteParam<string>('date', {
      patterns: routePaths,
      defaultValue: DEFAULT_DATE_RANGE,
    })

    this.depositsOnHoldParam = computedRouteParam<boolean | undefined>('depositsOnHold', {
      patterns: routePaths,
      defaultValue: false,
    })

    this.exactBedroomsFilterParam = computedRouteParam<string | undefined>('bedrooms', {
      patterns: routePaths,
    })

    this.highlightIdsFilterParam = computedRouteParam<string[] | undefined>('highlights', {
      patterns: routePaths,
      format: (values?: string[] | null) => (values ? values.join(',') : ''),
      parse: valueString => (valueString ? valueString.split(',') : undefined),
    })

    this.maxPriceFilterParam = computedRouteParam<string | undefined>('maxPrice', {
      patterns: routePaths,
    })

    this.minBathroomsFilterParam = computedRouteParam<string | undefined>('minBathrooms', {
      patterns: routePaths,
    })

    this.minBedroomsFilterParam = computedRouteParam<string | undefined>('minBedrooms', {
      patterns: routePaths,
    })

    this.minPriceFilterParam = computedRouteParam<string | undefined>('minPrice', {
      patterns: routePaths,
    })

    this.petsFilterParam = computedRouteParam<PetPolicyFieldKind | undefined>('pets', {
      patterns: routePaths,
    })

    this.propertyIdParam = computedRouteParam<string | undefined>('property-id', {
      patterns: routePaths,
    })

    this.revenueModelFilterParam = computedRouteParam<RevenueModel[] | undefined>('revenueModel', {
      patterns: routePaths,
      format: (values?: string[] | null) => (values ? values.join(',') : ''),
      parse: valueString => (valueString ? (valueString.split(',') as RevenueModel[]) : undefined),
    })

    this.riskLevelFilterParam = computedRouteParam<RiskLevel[] | undefined>('riskLevel', {
      patterns: routePaths,
      format: (values?: string[] | null) => (values ? values.join(',') : ''),
      parse: valueString => (valueString ? (valueString.split(',') as RiskLevel[]) : undefined),
    })

    this.seasonGroupsFilterParam = computedRouteParam<string[] | undefined>('season-groups', {
      patterns: routePaths,
      format: (values?: string[] | null) => (values ? values.join(',') : ''),
      parse: valueString => (valueString ? valueString.split(',') : undefined),
      defaultValue: undefined,
    })

    this.showNightlyRateFilterParam = computedRouteParam<boolean | undefined>('showNightlyRate', {
      patterns: routePaths,
      parse: JSON.parse,
      defaultValue: false,
    })

    this.minOccupancyParam = computedRouteParam<string | undefined>('minOccupancy', {
      patterns: routePaths,
    })

    this.maxOccupancyParam = computedRouteParam<string | undefined>('maxOccupancy', {
      patterns: routePaths,
    })
  }

  // overriding computed in mobx 5 is not supported
  get otherTapeChartFiltersFromUrl(): TapeChartSidebarFilters {
    return {
      amenities: this.amenityIdsFilterParam.get(),
      depositsOnHold: this.depositsOnHoldParam.get(),
      exactBedrooms: this.exactBedroomsFilterParam.get(),
      highlights: this.highlightIdsFilterParam.get(),
      issues: this.bookingIssuesFilterParam.get(),
      maxPrice: this.maxPriceFilterParam.get(),
      minBathrooms: this.minBathroomsFilterParam.get(),
      minBedrooms: this.minBedroomsFilterParam.get(),
      minPrice: this.minPriceFilterParam.get(),
      petsPolicy: this.petsFilterParam.get(),
      revenueModel: this.revenueModelFilterParam.get(),
      riskLevel: this.riskLevelFilterParam.get(),
      minOccupancy: this.minOccupancyParam.get(),
      maxOccupancy: this.maxOccupancyParam.get(),
    }
  }

  setActiveFiltersLength(value: number) {
    this.activeFiltersLength = value
  }

  setFiltersCache(filters: TapeChartSidebarFilters): void {
    extendObservable(this.filtersCache, filters)
  }

  setAmenityIdsFilter = (amenityIds?: string[]): void => {
    this.amenityIdsFilterParam.replace(amenityIds || undefined)
  }

  setBookingIssuesFilter(value?: BookingIssue[]): void {
    this.bookingIssuesFilterParam.replace(value)
  }

  setDateRangeFilter(date?: string): void {
    this.dateRangeFilterParam.replace(date || undefined)
  }

  setExactBedroomsFilter = (bathrooms?: string): void => {
    this.exactBedroomsFilterParam.push(bathrooms)
  }

  setSeasonGroupIdsFilter(regionIds?: string[]): void {
    this.seasonGroupsFilterParam.replace(regionIds)
  }

  setMinBathroomsFilter = (bathrooms?: string): void => {
    this.minBathroomsFilterParam.push(bathrooms)
  }

  setMinBedroomsFilter = (bedrooms?: string): void => {
    this.minBedroomsFilterParam.push(bedrooms)
  }

  setPetsFilter = (pets?: PetPolicyFieldKind): void => {
    this.petsFilterParam.replace(pets || undefined)
  }

  setDepositsOnHoldFilter(value?: boolean): void {
    this.depositsOnHoldParam.replace(value)
    this.filtersCache.depositsOnHold = value
  }

  setMinPriceFilter = (price?: string): void => {
    this.minPriceFilterParam.replace(price || undefined)
  }

  setMaxPriceFilter = (price?: string): void => {
    this.maxPriceFilterParam.replace(price || undefined)
  }

  setShowNightlyRateFilter(active?: boolean): void {
    this.showNightlyRateFilterParam.push(active || false)
  }

  setHighlightIdsFilter = (highlightIds?: string[]): void => {
    this.highlightIdsFilterParam.replace(highlightIds || undefined)
  }

  setRevenueModelFilter(value?: RevenueModel[]): void {
    this.revenueModelFilterParam.replace(value)
  }

  setRiskLevelFilter(value?: RiskLevel[]): void {
    this.riskLevelFilterParam.replace(value)
  }

  setMinOccupancy(value?: string): void {
    this.minOccupancyParam.replace(value)
  }

  setMaxOccupancy(value?: string): void {
    this.maxOccupancyParam.replace(value)
  }

  // MobX 5 is not supporting actions overriding
  superApplyCachedFilters() {
    const filtersValueFromUrl = this.otherTapeChartFiltersFromUrl

    if (!isEqual(this.filtersCache.amenities, filtersValueFromUrl?.amenities))
      this.setAmenityIdsFilter(this.filtersCache.amenities)

    if (!isEqual(this.filtersCache.period, filtersValueFromUrl?.period))
      this.setDateRangeFilter(this.filtersCache.period)

    if (!isEqual(this.filtersCache.depositsOnHold, filtersValueFromUrl?.depositsOnHold))
      this.setDepositsOnHoldFilter(this.filtersCache.depositsOnHold)

    if (!isEqual(this.filtersCache.exactBedrooms, filtersValueFromUrl?.exactBedrooms))
      this.setExactBedroomsFilter(this.filtersCache.exactBedrooms)

    if (!isEqual(this.filtersCache.highlights, filtersValueFromUrl?.highlights))
      this.setHighlightIdsFilter(this.filtersCache.highlights)

    if (this.filtersCache.maxPrice !== filtersValueFromUrl?.maxPrice)
      this.setMaxPriceFilter(this.filtersCache.maxPrice)

    if (this.filtersCache.minBedrooms !== filtersValueFromUrl?.minBedrooms)
      this.setMinBedroomsFilter(this.filtersCache.minBedrooms)

    if (this.filtersCache.minBathrooms !== filtersValueFromUrl?.minBathrooms)
      this.setMinBathroomsFilter(this.filtersCache.minBathrooms)

    if (this.filtersCache.minPrice !== filtersValueFromUrl?.minPrice)
      this.setMinPriceFilter(this.filtersCache.minPrice)

    if (this.filtersCache.petsPolicy !== filtersValueFromUrl?.petsPolicy)
      this.setPetsFilter(this.filtersCache.petsPolicy)

    if (!isEqual(this.filtersCache.seasonGroupsIds, filtersValueFromUrl?.seasonGroupsIds))
      this.setSeasonGroupIdsFilter(this.filtersCache.seasonGroupsIds)

    if (!isEqual(this.filtersCache.nightlyRate, filtersValueFromUrl?.nightlyRate))
      this.setShowNightlyRateFilter(this.filtersCache.nightlyRate)

    if (!isEqual(this.filtersCache.revenueModel, filtersValueFromUrl?.revenueModel))
      this.setRevenueModelFilter(this.filtersCache.revenueModel)

    if (!isEqual(this.filtersCache.riskLevel, filtersValueFromUrl?.riskLevel))
      this.setRiskLevelFilter(this.filtersCache.riskLevel)

    if (!isEqual(this.filtersCache.minOccupancy, filtersValueFromUrl?.minOccupancy))
      this.setMinOccupancy(this.filtersCache.minOccupancy)

    if (!isEqual(this.filtersCache.maxOccupancy, filtersValueFromUrl?.maxOccupancy))
      this.setMaxOccupancy(this.filtersCache.maxOccupancy)
  }

  // MobX 5 is not supporting actions overriding
  superClearOtherFilters() {
    this.setAmenityIdsFilter(undefined)
    this.setDepositsOnHoldFilter(false)
    this.setExactBedroomsFilter(undefined)
    this.setHighlightIdsFilter(undefined)
    this.setBookingIssuesFilter(undefined)
    this.setMaxPriceFilter('')
    this.setMinBathroomsFilter(undefined)
    this.setMinBedroomsFilter(undefined)
    this.setMinPriceFilter('')
    this.setPetsFilter(undefined)
    this.setShowNightlyRateFilter(undefined)
    this.setRevenueModelFilter(undefined)
    this.setRiskLevelFilter(undefined)
    this.setMinOccupancy(undefined)
    this.setMaxOccupancy(undefined)
  }

  clearFilterByKey = (filterKey: string) => {
    const sp = new URLSearchParams(RoutingStore.location.search)
    sp.delete(filterKey)
    RoutingStore.history.push({
      pathname: RoutingStore.location.pathname,
      search: sp.toString(),
    })
  }
}
